import { recordActivityView } from '../../services/activityHistoryService';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft, Calendar, Users, Building2, DollarSign, Clock,
  FileText, StickyNote, LayoutGrid, Plus, Trash2, X, Loader,
  Download, Upload,
} from 'lucide-react';
import Avatar from '../Avatar';
import {
  projectsService, projectTasksService, projectNotesService,
  projectDocumentsService, projectSubtasksService, projectTaskAttachmentsService,
  teamMembersService, supabase,
  ProjectTask, ProjectNote, ProjectDocument, ProjectSubtask, ProjectTaskAttachment,
} from '../../services/supabaseService';
import { emailNotificationService } from '../../services/emailNotificationService';
import { notificationService } from '../../services/notificationService';
import ProjectMaintenance from '../maintenance/ProjectMaintenance';
import TimesheetModal from '../collaborators/TimesheetModal';
import { timesheetsService } from '../../services/supabaseService';
import authService from '../../services/authService';
import { usePermissions } from '../../hooks/usePermissions';

interface Props {
  projectId: string;
  initialTaskId?: string;
  onBack: () => void;
}

const STATUS_COLS: Array<{ id: ProjectTask['status']; label: string; color: string }> = [
  { id: 'backlog',     label: 'Backlog',     color: 'bg-gray-100 text-gray-700 border-gray-300' },
  { id: 'in_progress', label: 'En curso',    color: 'bg-blue-100 text-blue-700 border-blue-300' },
  { id: 'in_review',   label: 'En revisión', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { id: 'done',        label: 'Hecho',       color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
];

const PRIORITY_DOT: Record<ProjectTask['priority'], string> = {
  low: 'bg-gray-400',
  medium: 'bg-blue-500',
  high: 'bg-red-500',
};

const ProjectDetailPage: React.FC<Props> = ({ projectId, onBack, initialTaskId }) => {
  const [activeTab, setActiveTab] = useState<'resumen' | 'tareas' | 'notas' | 'documentos' | 'horas' | 'mantenimiento'>('resumen');
  const [project, setProject] = useState<any>(null);
  const [team, setTeam] = useState<any[]>([]);
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [notes, setNotes] = useState<ProjectNote[]>([]);
  const [docs, setDocs] = useState<ProjectDocument[]>([]);
  const [timesheets, setTimesheets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Contadores por task_id para mostrar en las cards del Kanban.
  const [subtasksByTask, setSubtasksByTask] = useState<Record<string, ProjectSubtask[]>>({});
  const [attachmentsByTask, setAttachmentsByTask] = useState<Record<string, ProjectTaskAttachment[]>>({});

  // Datos económicos del proyecto (presupuesto, tarifa, horas contratadas,
  // coste total) sólo son para admin/superadmin. El programador no debe
  // saber cuánto factura Cero Ideas al cliente por su trabajo.
  const { isSuperAdmin } = usePermissions();
  const canSeeEconomics = isSuperAdmin();
  const currentUserId = authService.getCurrentSession()?.user?.id;

  const [taskModal, setTaskModal] = useState<{ open: boolean; task?: ProjectTask; defaultStatus?: ProjectTask['status'] }>({ open: false });
  const openedSupportTask=useRef('');
  useEffect(()=>{
    if(loading || !initialTaskId || openedSupportTask.current===initialTaskId) return;
    openedSupportTask.current=initialTaskId;
    const task=tasks.find(t=>t.id===initialTaskId && t.projectId===projectId);
    if(task){setActiveTab('tareas');setTaskModal({open:true,task});}
  },[loading,initialTaskId,tasks,projectId]);
  const [newNoteText, setNewNoteText] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Editor de parte de horas en overlay dentro del detalle del proyecto.
  const [openTimesheet, setOpenTimesheet] = useState<any | null>(null);
  const [timesheetSearch, setTimesheetSearch] = useState('');

  useEffect(() => {
    loadAll();
  }, [projectId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [projList, allMembers, tk, nt, dc, tsRes] = await Promise.all([
        projectsService.getAll(),
        teamMembersService.getAll(),
        projectTasksService.listByProject(projectId),
        projectNotesService.listByProject(projectId),
        projectDocumentsService.listByProject(projectId),
        supabase
          .from('timesheets')
          .select('*, users(id, name), projects(name)')
          .eq('project_id', projectId)
          .order('date', { ascending: false }),
      ]);
      const p = projList.find((x: any) => x.id === projectId);
      setProject(p || null);
      if (p) void recordActivityView('project', projectId);
      const teamIds: string[] = p?.assignedUsers || [];
      setTeam(allMembers.filter((m: any) => teamIds.includes(m.id)));
      setTasks(tk);
      setNotes(nt);
      setDocs(dc);
      setTimesheets((tsRes.data || []).map((row: any) => ({
        id: row.id,
        userId: row.user_id,
        userName: row.users?.name || 'Colaborador',
        date: row.date,
        hours: Number(row.hours || 0),
        appliedRate: Number(row.applied_rate || 0),
        calculatedAmount: Number(row.calculated_amount || 0),
        description: row.description || '',
        status: row.status || 'draft',
        createdAt: row.created_at,
      })));

      // Contadores por task para las cards del Kanban.
      const taskIds = tk.map(t => t.id);
      if (taskIds.length > 0) {
        const [subMap, attMap] = await Promise.all([
          projectSubtasksService.listByTasks(taskIds),
          projectTaskAttachmentsService.listByTasks(taskIds),
        ]);
        setSubtasksByTask(subMap);
        setAttachmentsByTask(attMap);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const tasksByStatus = useMemo(() => {
    const grouped: Record<ProjectTask['status'], ProjectTask[]> = {
      backlog: [], in_progress: [], in_review: [], done: [],
    };
    tasks.forEach(t => grouped[t.status].push(t));
    return grouped;
  }, [tasks]);

  const consumedHours = useMemo(
    () => tasks.filter(t => t.status === 'done').reduce((s, t) => s + (t.estimatedHours || 0), 0),
    [tasks]
  );

  // ------- task CRUD -------
  const handleSaveTask = async (data: Partial<ProjectTask>) => {
    try {
      if (taskModal.task) {
        const prev = taskModal.task;
        const updated = await projectTasksService.update(prev.id, data);
        setTasks(t => t.map(x => (x.id === updated.id ? updated : x)));
        // Aviso si el cambio de estado es crítico (in_review o done).
        if (updated.status !== prev.status && (updated.status === 'in_review' || updated.status === 'done')) {
          notifyTaskStatusChange(updated, prev.status);
        }
      } else {
        const created = await projectTasksService.create(projectId, {
          ...data,
          status: taskModal.defaultStatus || data.status || 'backlog',
        });
        setTasks(prev => [...prev, created]);
      }
      setTaskModal({ open: false });
    } catch (err: any) {
      alert('Error guardando tarea: ' + (err?.message || err));
    }
  };

  const handleDeleteTask = async (id: string) => {
    if (!confirm('¿Eliminar esta tarea?')) return;
    try {
      await projectTasksService.delete(id);
      setTasks(prev => prev.filter(t => t.id !== id));
    } catch (err: any) {
      alert('Error eliminando tarea: ' + (err?.message || err));
    }
  };

  const moveTask = async (task: ProjectTask, newStatus: ProjectTask['status']) => {
    if (task.status === newStatus) return;
    const optimistic = { ...task, status: newStatus };
    setTasks(prev => prev.map(t => (t.id === task.id ? optimistic : t)));
    try {
      const updated = await projectTasksService.update(task.id, { status: newStatus });
      setTasks(prev => prev.map(t => (t.id === task.id ? updated : t)));
      if (newStatus === 'in_review' || newStatus === 'done') {
        notifyTaskStatusChange(updated, task.status);
      }
    } catch (err) {
      // revert
      setTasks(prev => prev.map(t => (t.id === task.id ? task : t)));
      alert('Error moviendo tarea');
    }
  };

  // Notifica in-app + email al asignado cuando la tarea entra en un estado
  // crítico (in_review o done). No se auto-notifica al mismo user que hace
  // el cambio.
  const notifyTaskStatusChange = async (updated: ProjectTask, prevStatus: ProjectTask['status']) => {
    if (!updated.assigneeId) return;
    if (updated.assigneeId === currentUserId) return;
    const assignee = team.find(m => m.id === updated.assigneeId);
    if (!assignee) return;

    const statusLabels: Record<ProjectTask['status'], string> = {
      backlog: 'Backlog',
      in_progress: 'En curso',
      in_review: 'En revisión',
      done: 'Hecho',
    };
    const newLabel = statusLabels[updated.status];
    const prevLabel = statusLabels[prevStatus];
    const projectName = project?.name || 'Proyecto';

    // In-app
    try {
      await notificationService.createNotification({
        user_id: assignee.id,
        code: 'TASK.STATUS_CHANGED',
        title: `Tarea "${updated.title}" → ${newLabel}`,
        body: `${projectName} · pasó de ${prevLabel} a ${newLabel}`,
        severity: 'info',
        data: {
          task_id: updated.id,
          project_id: projectId,
          project_name: projectName,
          new_status: updated.status,
          prev_status: prevStatus,
        },
      });
    } catch (err) {
      console.error('Error notif in-app cambio estado tarea:', err);
    }

    // Email
    if (assignee.email) {
      try {
        await emailNotificationService.sendTaskStatusChangedEmail(
          assignee.name || 'Colaborador',
          assignee.email,
          updated.title,
          projectName,
          prevLabel,
          newLabel,
          `${window.location.origin}/pagos`
        );
      } catch (err) {
        console.error('Error email cambio estado tarea:', err);
      }
    }
  };

  // ------- notas -------
  const handleAddNote = async () => {
    const body = newNoteText.trim();
    if (!body) return;
    try {
      const created = await projectNotesService.create(projectId, body);
      setNotes(prev => [created, ...prev]);
      setNewNoteText('');
    } catch (err: any) {
      alert('Error creando nota: ' + (err?.message || err));
    }
  };

  const handleDeleteNote = async (id: string) => {
    if (!confirm('¿Eliminar esta nota?')) return;
    await projectNotesService.delete(id);
    setNotes(prev => prev.filter(n => n.id !== id));
  };

  // ------- documentos -------
  const handleUploadDoc = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const doc = await projectDocumentsService.upload(projectId, file);
      setDocs(prev => [doc, ...prev]);
    } catch (err: any) {
      alert('Error subiendo documento: ' + (err?.message || err));
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  const handleDownloadDoc = async (doc: ProjectDocument) => {
    const url = await projectDocumentsService.getSignedUrl(doc.storagePath);
    if (url) window.open(url, '_blank');
  };

  const handleDeleteDoc = async (doc: ProjectDocument) => {
    if (!confirm(`¿Eliminar "${doc.name}"?`)) return;
    try {
      await projectDocumentsService.delete(doc);
      setDocs(prev => prev.filter(d => d.id !== doc.id));
    } catch (err: any) {
      alert('Error eliminando documento: ' + (err?.message || err));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" /> Volver a Proyectos
        </button>
        <p className="mt-6 text-gray-500">Proyecto no encontrado.</p>
      </div>
    );
  }

  // Contador rojo de partes de horas pendientes (status='submitted').
  const pendingTimesheets = timesheets.filter(t => t.status === 'submitted').length;

  const tabs: Array<{ id: typeof activeTab; label: string; icon: React.ElementType; count?: number; badge?: number }> = [
    { id: 'resumen',    label: 'Resumen',    icon: LayoutGrid },
    { id: 'tareas',     label: 'Tareas',     icon: Clock,      count: tasks.length },
    { id: 'horas',      label: 'Horas trabajadas', icon: Clock, count: timesheets.length, badge: canSeeEconomics ? pendingTimesheets : 0 },
    { id: 'mantenimiento', label: 'Mantenimiento', icon: Clock },
    { id: 'notas',      label: 'Notas',      icon: StickyNote, count: notes.length },
    { id: 'documentos', label: 'Documentos', icon: FileText,   count: docs.length },
  ];

  return (
    <div className="p-6">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Volver a Proyectos
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="h-14 w-14 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Building2 className="h-7 w-7 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 truncate">{project.name}</h1>
              <p className="text-sm text-gray-500 truncate">{project.clientName || 'Sin cliente'}</p>
            </div>
          </div>
          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
            project.status === 'active' ? 'bg-green-100 text-green-800' :
            project.status === 'completed' ? 'bg-blue-100 text-blue-800' :
            project.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {project.status === 'active' ? 'Activo' :
             project.status === 'completed' ? 'Completado' :
             project.status === 'paused' ? 'Pausado' : project.status}
          </span>
        </div>

        {project.description && (
          <p className="text-sm text-gray-700 mt-4 whitespace-pre-wrap">{project.description}</p>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-6 px-6">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                  {typeof tab.count === 'number' && (
                    <span className="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
                      {tab.count}
                    </span>
                  )}
                  {tab.badge != null && tab.badge > 0 && (
                    <span className="text-xs bg-red-500 text-white rounded-full px-2 py-0.5 font-semibold">
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'resumen' && (
            <ResumenTab
              project={project}
              team={team}
              tasks={tasks}
              consumedHours={consumedHours}
              canSeeEconomics={canSeeEconomics}
              currentUserId={currentUserId}
            />
          )}

          {activeTab === 'tareas' && (
            <KanbanTab
              tasks={tasksByStatus}
              team={team}
              currentUserId={currentUserId}
              subtasksByTask={subtasksByTask}
              attachmentsByTask={attachmentsByTask}
              onNewTask={(status) => setTaskModal({ open: true, defaultStatus: status })}
              onEditTask={(task) => setTaskModal({ open: true, task })}
              onDeleteTask={handleDeleteTask}
              onMove={moveTask}
            />
          )}

          {activeTab === 'notas' && (
            <NotesTab
              notes={notes}
              value={newNoteText}
              onChange={setNewNoteText}
              onAdd={handleAddNote}
              onDelete={handleDeleteNote}
            />
          )}

          {activeTab === 'documentos' && (
            <DocsTab
              docs={docs}
              uploading={uploadingDoc}
              onUpload={handleUploadDoc}
              onDownload={handleDownloadDoc}
              onDelete={handleDeleteDoc}
            />
          )}

          {activeTab === 'mantenimiento' && <ProjectMaintenance projectId={projectId} projectName={project.name} clientName={project.clientName} team={team} canManage={canSeeEconomics} />}

          {activeTab === 'horas' && (
            <TimesheetsTab
              timesheets={timesheets}
              canSeeEconomics={canSeeEconomics}
              search={timesheetSearch}
              onSearchChange={setTimesheetSearch}
              onOpenTimesheet={(id) => {
                const t = timesheets.find(x => x.id === id);
                if (!t) return;
                // Preparo el objeto en la shape que TimesheetModal espera.
                setOpenTimesheet({
                  ...t,
                  collaboratorId: t.userId,
                  collaboratorName: t.userName,
                  projectId,
                  projectName: project?.name,
                  date: new Date(t.date),
                });
              }}
            />
          )}
        </div>
      </div>

      {taskModal.open && (
        <TaskModal
          task={taskModal.task}
          defaultStatus={taskModal.defaultStatus}
          team={team}
          onCancel={() => setTaskModal({ open: false })}
          onSave={handleSaveTask}
        />
      )}

      {openTimesheet && (
        <TimesheetModal
          isOpen={true}
          onClose={() => setOpenTimesheet(null)}
          timesheet={openTimesheet}
          onSave={async (data: any) => {
            try {
              await timesheetsService.update(openTimesheet.id, {
                userId: data.userId,
                projectId: data.projectId,
                date: data.date,
                hours: data.hours,
                appliedRate: data.appliedRate,
                calculatedAmount: data.calculatedAmount,
                description: data.description,
                status: data.status,
              });
              setOpenTimesheet(null);
              await loadAll(); // refresca los timesheets y el badge de pendientes
            } catch (err: any) {
              alert('Error actualizando parte: ' + (err?.message || err));
            }
          }}
        />
      )}
    </div>
  );
};

// ================================================================
// Tabs
// ================================================================
const ResumenTab: React.FC<{
  project: any;
  team: any[];
  tasks: ProjectTask[];
  consumedHours: number;
  canSeeEconomics: boolean;
  currentUserId?: string;
}> = ({ project, team, tasks, consumedHours, canSeeEconomics, currentUserId }) => {
  const budget = Number(project.budget || 0);
  const totalHours = Number(project.totalHours || project.total_hours || 0);
  const startDate = project.startDate || project.start_date;
  const endDate = project.endDate || project.end_date;
  const techStack = project.techStack || project.tech_stack;

  // Stats se adaptan según permisos. El colaborador NO ve presupuesto ni
  // tarifa: sólo su propio compromiso (tareas suyas, horas suyas).
  const myTasks = tasks.filter(t => t.assigneeId === currentUserId);
  const myHours = myTasks.filter(t => t.status === 'done').reduce((s, t) => s + (t.estimatedHours || 0), 0);

  const stats = canSeeEconomics
    ? [
        { label: 'Presupuesto', value: budget > 0 ? `${budget.toLocaleString('es-ES')} €` : '—', icon: DollarSign },
        { label: 'Horas contratadas', value: totalHours > 0 ? `${totalHours}h` : '—', icon: Clock },
        { label: 'Horas consumidas', value: `${consumedHours}h`, icon: Clock },
        { label: 'Tareas totales', value: String(tasks.length), icon: LayoutGrid },
      ]
    : [
        { label: 'Mis tareas', value: String(myTasks.length), icon: LayoutGrid },
        { label: 'Completadas', value: String(myTasks.filter(t => t.status === 'done').length), icon: LayoutGrid },
        { label: 'Mis horas', value: `${myHours}h`, icon: Clock },
        { label: 'Equipo', value: String(team.length), icon: Users },
      ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map(s => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-xs text-gray-500 uppercase font-medium">
                <Icon className="w-3.5 h-3.5" /> {s.label}
              </div>
              <p className="text-xl font-semibold text-gray-900 mt-2">{s.value}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3">Fechas</h3>
          <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-gray-500 flex items-center gap-2"><Calendar className="w-4 h-4" /> Inicio</span>
              <span className="text-gray-900">{startDate ? new Date(startDate).toLocaleDateString('es-ES') : '—'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500 flex items-center gap-2"><Calendar className="w-4 h-4" /> Fin previsto</span>
              <span className="text-gray-900">{endDate ? new Date(endDate).toLocaleDateString('es-ES') : '—'}</span>
            </div>
            {techStack && (
              <div className="pt-2 border-t border-gray-200">
                <p className="text-xs text-gray-500 uppercase mb-1">Tech stack</p>
                <p className="text-sm text-gray-900">{techStack}</p>
              </div>
            )}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3">Equipo ({team.length})</h3>
          <div className="bg-gray-50 rounded-lg p-4">
            {team.length === 0 ? (
              <p className="text-sm text-gray-500">Sin colaboradores asignados</p>
            ) : (
              <div className="space-y-2">
                {team.map(m => (
                  <div key={m.id} className={`flex items-center gap-3 ${m.id === currentUserId ? 'bg-blue-50 -mx-2 px-2 py-1 rounded' : ''}`}>
                    <Avatar name={m.name} src={m.avatar} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {m.name}
                        {m.id === currentUserId && <span className="ml-2 text-xs text-blue-600">(tú)</span>}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{m.email}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const KanbanTab: React.FC<{
  tasks: Record<ProjectTask['status'], ProjectTask[]>;
  team: any[];
  currentUserId?: string;
  subtasksByTask: Record<string, ProjectSubtask[]>;
  attachmentsByTask: Record<string, ProjectTaskAttachment[]>;
  onNewTask: (status: ProjectTask['status']) => void;
  onEditTask: (task: ProjectTask) => void;
  onDeleteTask: (id: string) => void;
  onMove: (task: ProjectTask, newStatus: ProjectTask['status']) => void;
}> = ({ tasks, team, currentUserId, subtasksByTask, attachmentsByTask, onNewTask, onEditTask, onDeleteTask, onMove }) => {

  const onDragStart = (e: React.DragEvent, task: ProjectTask) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ id: task.id, status: task.status }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDrop = (e: React.DragEvent, newStatus: ProjectTask['status']) => {
    e.preventDefault();
    try {
      const raw = e.dataTransfer.getData('text/plain');
      const { id } = JSON.parse(raw);
      const source = Object.values(tasks).flat().find(t => t.id === id);
      if (source) onMove(source, newStatus);
    } catch {}
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {STATUS_COLS.map(col => (
        <div
          key={col.id}
          className="bg-gray-50 rounded-lg border border-gray-200 flex flex-col min-h-[400px]"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => onDrop(e, col.id)}
        >
          <div className={`px-3 py-2 border-b border-gray-200 flex items-center justify-between rounded-t-lg ${col.color}`}>
            <span className="text-sm font-semibold">{col.label}</span>
            <span className="text-xs bg-white bg-opacity-70 rounded-full px-2 py-0.5">{tasks[col.id].length}</span>
          </div>
          <div className="p-2 flex-1 space-y-2 overflow-y-auto">
            {tasks[col.id].map(t => {
              const mine = currentUserId && t.assigneeId === currentUserId;
              return (
              <div
                key={t.id}
                draggable
                onDragStart={(e) => onDragStart(e, t)}
                onClick={() => onEditTask(t)}
                className={`border rounded-lg p-3 cursor-pointer hover:shadow-sm transition-shadow group ${
                  mine ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-sm font-medium text-gray-900 line-clamp-2 flex-1">{t.title}</p>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDeleteTask(t.id); }}
                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-opacity"
                    title="Eliminar"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                {t.description && (
                  <p className="text-xs text-gray-500 line-clamp-2 mb-2">{t.description}</p>
                )}
                {(() => {
                  const subs = subtasksByTask[t.id] || [];
                  const done = subs.filter(s => s.completed).length;
                  const attsCount = (attachmentsByTask[t.id] || []).length;
                  return (subs.length > 0 || attsCount > 0) ? (
                    <div className="flex items-center gap-3 text-xs text-gray-500 mb-1.5">
                      {subs.length > 0 && (
                        <span title={`${done}/${subs.length} subtareas completadas`} className="flex items-center gap-1">
                          <span>☑</span>{done}/{subs.length}
                        </span>
                      )}
                      {attsCount > 0 && (
                        <span title={`${attsCount} adjuntos`} className="flex items-center gap-1">
                          📎 {attsCount}
                        </span>
                      )}
                    </div>
                  ) : null;
                })()}
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${PRIORITY_DOT[t.priority]}`} title={t.priority} />
                    {t.dueDate && (
                      <span className="text-gray-500">{new Date(t.dueDate).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}</span>
                    )}
                    {t.estimatedHours != null && (
                      <span className="text-gray-500">· {t.estimatedHours}h</span>
                    )}
                  </div>
                  {t.assignee && <Avatar name={t.assignee.name} src={t.assignee.avatar} size="xs" />}
                </div>
              </div>
              );
            })}
          </div>
          <button
            onClick={() => onNewTask(col.id)}
            className="mx-2 mb-2 flex items-center justify-center gap-1 py-1.5 text-xs text-gray-500 hover:text-blue-600 border border-dashed border-gray-300 hover:border-blue-400 rounded transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Añadir tarea
          </button>
        </div>
      ))}
    </div>
  );
};

const NotesTab: React.FC<{
  notes: ProjectNote[];
  value: string;
  onChange: (v: string) => void;
  onAdd: () => void;
  onDelete: (id: string) => void;
}> = ({ notes, value, onChange, onAdd, onDelete }) => {
  const currentUser = authService.getCurrentSession()?.user;
  return (
    <div>
      <div className="mb-6">
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          placeholder="Escribe una nota…"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
        <div className="flex justify-end mt-2">
          <button
            onClick={onAdd}
            disabled={!value.trim()}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            Añadir nota
          </button>
        </div>
      </div>

      {notes.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8">Aún no hay notas en este proyecto.</p>
      ) : (
        <div className="space-y-3">
          {notes.map(n => (
            <div key={n.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-xs text-gray-500">
                  <span className="font-medium text-gray-700">{n.authorName || 'Alguien'}</span>
                  {' · '}
                  {n.createdAt.toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
                {(n.authorId === currentUser?.id) && (
                  <button onClick={() => onDelete(n.id)} className="text-gray-400 hover:text-red-600" title="Eliminar">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{n.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const formatSize = (bytes?: number) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const DocsTab: React.FC<{
  docs: ProjectDocument[];
  uploading: boolean;
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDownload: (doc: ProjectDocument) => void;
  onDelete: (doc: ProjectDocument) => void;
}> = ({ docs, uploading, onUpload, onDownload, onDelete }) => (
  <div>
    <div className="mb-6">
      <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 cursor-pointer">
        {uploading ? <Loader className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {uploading ? 'Subiendo…' : 'Subir documento'}
        <input type="file" onChange={onUpload} className="hidden" disabled={uploading} />
      </label>
    </div>

    {docs.length === 0 ? (
      <p className="text-sm text-gray-500 text-center py-8">Aún no hay documentos en este proyecto.</p>
    ) : (
      <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg">
        {docs.map(d => (
          <div key={d.id} className="flex items-center justify-between p-4 hover:bg-gray-50">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{d.name}</p>
                <p className="text-xs text-gray-500">
                  {formatSize(d.sizeBytes)}
                  {d.uploaderName && ` · Subido por ${d.uploaderName}`}
                  {' · '}{d.createdAt.toLocaleDateString('es-ES')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => onDownload(d)} className="p-2 text-blue-600 hover:bg-blue-50 rounded" title="Descargar">
                <Download className="w-4 h-4" />
              </button>
              <button onClick={() => onDelete(d)} className="p-2 text-red-600 hover:bg-red-50 rounded" title="Eliminar">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// ================================================================
// Timesheets del proyecto
// ================================================================
const TIMESHEET_STATUS: Record<string, { label: string; className: string }> = {
  draft:     { label: 'Borrador',   className: 'bg-gray-100 text-gray-700' },
  submitted: { label: 'Enviado',    className: 'bg-amber-100 text-amber-800' },
  approved:  { label: 'Aprobado',   className: 'bg-emerald-100 text-emerald-700' },
  rejected:  { label: 'Rechazado',  className: 'bg-red-100 text-red-700' },
};

const TimesheetsTab: React.FC<{
  timesheets: Array<any>;
  canSeeEconomics: boolean;
  search: string;
  onSearchChange: (v: string) => void;
  onOpenTimesheet: (id: string) => void;
}> = ({ timesheets, canSeeEconomics, search, onSearchChange, onOpenTimesheet }) => {
  if (timesheets.length === 0) {
    return (
      <div className="text-center py-12 text-sm text-gray-500">
        Aún no hay partes de horas para este proyecto.
      </div>
    );
  }

  const totalHours = timesheets.reduce((s, t) => s + (t.hours || 0), 0);
  const totalAmount = timesheets.reduce((s, t) => s + (t.calculatedAmount || 0), 0);
  const submitted = timesheets.filter(t => t.status === 'submitted').length;

  const filtered = search.trim()
    ? timesheets.filter(t =>
        (t.userName || '').toLowerCase().includes(search.toLowerCase()) ||
        (t.status || '').toLowerCase().includes(search.toLowerCase()) ||
        (t.description || '').toLowerCase().includes(search.toLowerCase())
      )
    : timesheets;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total partes" value={String(timesheets.length)} />
        <StatCard label="Horas registradas" value={`${totalHours}h`} />
        {canSeeEconomics && <StatCard label="Coste acumulado" value={`${totalAmount.toLocaleString('es-ES')} €`} />}
        <StatCard label="Pendientes de revisar" value={String(submitted)} highlight={submitted > 0} />
      </div>

      <input
        type="search"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Buscar por colaborador, estado o descripción…"
        className="w-full md:max-w-sm px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
      />

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-2 text-left">Colaborador</th>
              <th className="px-4 py-2 text-left">Fecha</th>
              <th className="px-4 py-2 text-right">Horas</th>
              {canSeeEconomics && <th className="px-4 py-2 text-right">Importe</th>}
              <th className="px-4 py-2 text-left">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={canSeeEconomics ? 5 : 4} className="px-4 py-6 text-center text-gray-400 text-sm">
                  Sin resultados para "{search}".
                </td>
              </tr>
            )}
            {filtered.map(t => {
              const st = TIMESHEET_STATUS[t.status] || TIMESHEET_STATUS.draft;
              return (
                <tr
                  key={t.id}
                  onClick={() => onOpenTimesheet(t.id)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-4 py-2 text-gray-900">{t.userName}</td>
                  <td className="px-4 py-2 text-gray-600">
                    {t.date ? new Date(t.date).toLocaleDateString('es-ES') : '—'}
                  </td>
                  <td className="px-4 py-2 text-right font-medium">{t.hours}h</td>
                  {canSeeEconomics && (
                    <td className="px-4 py-2 text-right">{Number(t.calculatedAmount).toLocaleString('es-ES')} €</td>
                  )}
                  <td className="px-4 py-2">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${st.className}`}>
                      {st.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className={`rounded-lg p-3 border ${highlight ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
    <p className={`text-xs uppercase font-medium ${highlight ? 'text-red-600' : 'text-gray-500'}`}>{label}</p>
    <p className={`text-lg font-semibold mt-1 ${highlight ? 'text-red-700' : 'text-gray-900'}`}>{value}</p>
  </div>
);

// ================================================================
// Task modal
// ================================================================
const TaskModal: React.FC<{
  task?: ProjectTask;
  defaultStatus?: ProjectTask['status'];
  team: any[];
  onCancel: () => void;
  onSave: (data: Partial<ProjectTask>) => void;
}> = ({ task, defaultStatus, team, onCancel, onSave }) => {
  const [form, setForm] = useState<Partial<ProjectTask>>(task || {
    title: '',
    description: '',
    status: defaultStatus || 'backlog',
    priority: 'medium',
  });

  // Subtareas y adjuntos sólo se pueden gestionar cuando la tarea existe
  // (necesitan task_id). Al crear una tarea nueva, sale un aviso.
  const [subtasks, setSubtasks] = useState<ProjectSubtask[]>([]);
  const [attachments, setAttachments] = useState<ProjectTaskAttachment[]>([]);
  const [loadingExtras, setLoadingExtras] = useState(false);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [newSubtaskDescription, setNewSubtaskDescription] = useState('');
  const [newSubtaskHours, setNewSubtaskHours] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!task?.id) return;
    (async () => {
      setLoadingExtras(true);
      try {
        const [subs, atts] = await Promise.all([
          projectSubtasksService.listByTask(task.id),
          projectTaskAttachmentsService.listByTask(task.id),
        ]);
        setSubtasks(subs);
        setAttachments(atts);
      } finally {
        setLoadingExtras(false);
      }
    })();
  }, [task?.id]);

  const handleAddSubtask = async () => {
    if (!task?.id || !newSubtaskTitle.trim()) return;
    try {
      const hoursNum = newSubtaskHours ? parseFloat(newSubtaskHours) : undefined;
      const created = await projectSubtasksService.create(task.id, {
        title: newSubtaskTitle.trim(),
        description: newSubtaskDescription.trim() || undefined,
        estimatedHours: hoursNum && !isNaN(hoursNum) ? hoursNum : undefined,
        position: subtasks.length,
      });
      setSubtasks(prev => [...prev, created]);
      setNewSubtaskTitle('');
      setNewSubtaskDescription('');
      setNewSubtaskHours('');
    } catch (err: any) {
      alert('Error creando subtarea: ' + (err?.message || err));
    }
  };

  const handleToggleSubtask = async (sub: ProjectSubtask) => {
    try {
      const updated = await projectSubtasksService.update(sub.id, { completed: !sub.completed });
      setSubtasks(prev => prev.map(s => (s.id === sub.id ? updated : s)));
    } catch (err: any) {
      alert('Error actualizando subtarea: ' + (err?.message || err));
    }
  };

  const handleUpdateSubtask = async (sub: ProjectSubtask, patch: Partial<ProjectSubtask>) => {
    try {
      const updated = await projectSubtasksService.update(sub.id, patch);
      setSubtasks(prev => prev.map(s => (s.id === sub.id ? updated : s)));
    } catch (err: any) {
      console.error('Error actualizando subtarea:', err);
    }
  };

  const handleDeleteSubtask = async (id: string) => {
    try {
      await projectSubtasksService.delete(id);
      setSubtasks(prev => prev.filter(s => s.id !== id));
    } catch (err: any) {
      alert('Error eliminando subtarea: ' + (err?.message || err));
    }
  };

  const handleUploadAttachment = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !task?.id) return;
    setUploading(true);
    try {
      const att = await projectTaskAttachmentsService.upload(task.id, file);
      setAttachments(prev => [att, ...prev]);
    } catch (err: any) {
      alert('Error subiendo adjunto: ' + (err?.message || err));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleOpenAttachment = async (att: ProjectTaskAttachment) => {
    const url = await projectTaskAttachmentsService.getSignedUrl(att.storagePath);
    if (url) window.open(url, '_blank');
  };

  const handleDeleteAttachment = async (att: ProjectTaskAttachment) => {
    if (!confirm(`¿Eliminar "${att.name}"?`)) return;
    try {
      await projectTaskAttachmentsService.delete(att);
      setAttachments(prev => prev.filter(a => a.id !== att.id));
    } catch (err: any) {
      alert('Error eliminando adjunto: ' + (err?.message || err));
    }
  };

  const totalSubtaskHours = subtasks.reduce((s, x) => s + (x.estimatedHours || 0), 0);
  const doneSubtasks = subtasks.filter(s => s.completed).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{task ? 'Editar tarea' : 'Nueva tarea'}</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
            <input
              type="text"
              required
              value={form.title || ''}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <textarea
              rows={3}
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as ProjectTask['status'] })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {STATUS_COLS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value as ProjectTask['priority'] })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="low">Baja</option>
                <option value="medium">Media</option>
                <option value="high">Alta</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Asignada a</label>
              <select
                value={form.assigneeId || ''}
                onChange={(e) => setForm({ ...form, assigneeId: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Sin asignar</option>
                {team.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Horas estimadas</label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={form.estimatedHours ?? ''}
                onChange={(e) => setForm({ ...form, estimatedHours: e.target.value ? parseFloat(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha límite</label>
              <input
                type="date"
                value={form.dueDate || ''}
                onChange={(e) => setForm({ ...form, dueDate: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          {task?.id ? (
            <>
              {/* Subtareas */}
              <div className="pt-2 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Subtareas
                    {subtasks.length > 0 && (
                      <span className="ml-2 text-xs text-gray-500">
                        {doneSubtasks}/{subtasks.length} · {totalSubtaskHours}h estimadas
                      </span>
                    )}
                  </label>
                </div>

                {loadingExtras ? (
                  <p className="text-xs text-gray-500">Cargando…</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {subtasks.map(sub => (
                      <SubtaskRow
                        key={sub.id}
                        subtask={sub}
                        onToggle={() => handleToggleSubtask(sub)}
                        onUpdate={handleUpdateSubtask}
                        onDelete={() => handleDeleteSubtask(sub.id)}
                      />
                    ))}
                  </div>
                )}

                <div className="mt-3 border border-dashed border-gray-300 rounded-lg p-3 space-y-2">
                  <input
                    type="text"
                    value={newSubtaskTitle}
                    onChange={(e) => setNewSubtaskTitle(e.target.value)}
                    placeholder="Título de la nueva subtarea…"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <textarea
                    rows={2}
                    value={newSubtaskDescription}
                    onChange={(e) => setNewSubtaskDescription(e.target.value)}
                    placeholder="Descripción (opcional)"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex items-center justify-between gap-2">
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={newSubtaskHours}
                      onChange={(e) => setNewSubtaskHours(e.target.value)}
                      placeholder="Horas"
                      className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={handleAddSubtask}
                      disabled={!newSubtaskTitle.trim()}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" /> Añadir
                    </button>
                  </div>
                </div>
              </div>

              {/* Adjuntos */}
              <div className="pt-2 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Adjuntos {attachments.length > 0 && <span className="text-xs text-gray-500">({attachments.length})</span>}
                  </label>
                  <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg cursor-pointer ${
                    uploading ? 'bg-gray-100 text-gray-500' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                  }`}>
                    {uploading ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    {uploading ? 'Subiendo…' : 'Subir archivo'}
                    <input
                      type="file"
                      onChange={handleUploadAttachment}
                      disabled={uploading}
                      className="hidden"
                    />
                  </label>
                </div>

                {attachments.length === 0 ? (
                  <p className="text-xs text-gray-400">Sin adjuntos.</p>
                ) : (
                  <div className="space-y-1">
                    {attachments.map(att => {
                      const isImage = att.mimeType?.startsWith('image/');
                      return (
                        <div key={att.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg group">
                          <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                            {isImage ? <FileText className="w-4 h-4 text-blue-600" /> : <FileText className="w-4 h-4 text-blue-600" />}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleOpenAttachment(att)}
                            className="flex-1 text-left min-w-0"
                          >
                            <p className="text-sm font-medium text-gray-900 truncate">{att.name}</p>
                            <p className="text-xs text-gray-500">
                              {att.sizeBytes != null && `${(att.sizeBytes / 1024).toFixed(1)} KB`}
                              {att.createdAt && ` · ${att.createdAt.toLocaleDateString('es-ES')}`}
                            </p>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteAttachment(att)}
                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-opacity"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="pt-2 border-t border-gray-200">
              <p className="text-xs text-gray-500 italic">
                Guarda la tarea primero para poder añadir subtareas y adjuntos.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
              Cancelar
            </button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              {task ? 'Guardar' : 'Crear tarea'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Fila de subtarea editable inline: título + descripción + horas.
const SubtaskRow: React.FC<{
  subtask: ProjectSubtask;
  onToggle: () => void;
  onUpdate: (sub: ProjectSubtask, patch: Partial<ProjectSubtask>) => void;
  onDelete: () => void;
}> = ({ subtask, onToggle, onUpdate, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState(subtask.title);
  const [description, setDescription] = useState(subtask.description || '');
  const [hours, setHours] = useState(subtask.estimatedHours != null ? String(subtask.estimatedHours) : '');

  useEffect(() => {
    setTitle(subtask.title);
    setDescription(subtask.description || '');
    setHours(subtask.estimatedHours != null ? String(subtask.estimatedHours) : '');
  }, [subtask]);

  const saveIfChanged = () => {
    const patch: Partial<ProjectSubtask> = {};
    const t = title.trim();
    if (t && t !== subtask.title) patch.title = t;
    if (description !== (subtask.description || '')) patch.description = description || undefined;
    const h = hours ? parseFloat(hours) : undefined;
    if (h !== subtask.estimatedHours) patch.estimatedHours = h != null && !isNaN(h) ? h : undefined;
    if (Object.keys(patch).length > 0) onUpdate(subtask, patch);
  };

  return (
    <div className="border border-gray-200 rounded-lg p-2 group hover:bg-gray-50">
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={subtask.completed}
          onChange={onToggle}
          className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <div className="flex-1 min-w-0">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={saveIfChanged}
            className={`w-full text-sm bg-transparent border-none px-0 focus:ring-0 focus:outline-none ${
              subtask.completed ? 'line-through text-gray-400' : 'text-gray-900'
            }`}
          />
          {(expanded || subtask.description) && (
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={saveIfChanged}
              placeholder="Descripción (opcional)"
              className="w-full mt-1 text-xs px-2 py-1 border border-gray-200 rounded focus:ring-1 focus:ring-blue-500"
            />
          )}
        </div>
        <input
          type="number"
          min="0"
          step="0.5"
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          onBlur={saveIfChanged}
          placeholder="h"
          className="w-16 px-2 py-0.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500"
          title="Horas estimadas"
        />
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-700 transition-opacity text-xs"
          title="Editar descripción"
        >
          {expanded ? '−' : '✎'}
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-opacity"
          title="Eliminar subtarea"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default ProjectDetailPage;
